import { CallVideosLayout } from 'app/components/call/CallVideos/CallVideosLayout';
import makeLazyComponent from 'app/components/layout/LazyLoadingComponent';
import OnWindowEvent from 'app/components/layout/OnWindowEvent';
import { useStickyHeightControl } from 'app/components/layout/Page/StickyContext';
import useMeasure from 'app/components/layout/useMeasure';
import { PURE_WHITE } from 'app/constants/Colors';
import { HEADER_HEIGHT } from 'app/constants/HeaderStyles';
import {
  CallBar,
  CallTab,
  CallVideoPlacement,
  CALL_SIDEBAR_WIDTH,
} from 'app/models/ui/call/constants';
import { useCss } from 'app/utils/css';
import { getWindowScrollPosition } from 'app/utils/env';
import React, { Fragment, useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { useCallBreakpoint } from 'app/containers/call/CallBreakpoint';
import EndLessonNotifier from 'app/components/call/BookingDetails/LessonBookingDetails/EndLessonNotifier';

const CallVideosFull = makeLazyComponent(() => import('app/components/call/CallVideos/CallVideosFull'));
const CallVideosHorizontal = makeLazyComponent(() => import('app/components/call/CallVideos/CallVideosHorizontal'));
const CallTabContent = makeLazyComponent(() => import('app/components/call/CallTabContent'));

/**
 * Order of precedence for stacking of containers. It is imperative that each container all be within the same stacking
 * context.
 *
 * Overhead content is used to position notifications, so it must be able to overlay in content videos.
 */
enum Stack {
  ChildContainer = 1,
  FullVideoContainer = 2,
  OverheadContainer = 3,
}

interface IProps {
  children: React.ReactNode;
  zIndex: number;

  // Szymon: This disables the narrow horizontal strip that's shown
  // when scrolled down, over the content, on mobile layouts.
  // On desktop layouts videos move to the sidebar when scrolling down
  // so for example, if pages is not scrollable but the whole page is
  // the video grid, this is not needed. So only places where this flag
  // makes sense is the <FullScreenCallPage>
  disableFixedHeaderVideo?: boolean;
}

const StickyContainer = React.memo<IProps>(({ children, disableFixedHeaderVideo, zIndex }) => {
  const { bar, tab, pushContentDown, bigVideo, videoPlacement } = useCallBreakpoint();
  const [isFixed, setIsFixed] = useState(false);
  const stickyHeightControl = useStickyHeightControl();
  const placeholderNode = useRef<HTMLDivElement>(null);

  const [extraOverheadContentContainerSize, extraOverheadContentContainerRef] = useMeasure();
  const [childContainerSize, childContainerRef] = useMeasure();

  const handleScroll = useCallback(() => {
    if (placeholderNode.current) {
      const { offsetTop } = placeholderNode.current;

      const { y } = getWindowScrollPosition();
      const shouldBeFixed = y > offsetTop;

      if (isFixed !== shouldBeFixed) {
        setIsFixed(shouldBeFixed);
      }
    }
  }, [isFixed]);

  useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  const stickyHeight = extraOverheadContentContainerSize.height + childContainerSize.height;

  useLayoutEffect(() => {
    stickyHeightControl.updateStickyHeight(stickyHeight);
  }, [stickyHeight, stickyHeightControl]);

  const fixedSidebar = bar === CallBar.Side;
  const onAFullScreenTab = bar === CallBar.Top && tab !== CallTab.Content;

  const showOverheadVideo =
    videoPlacement === CallVideoPlacement.Sticky && !onAFullScreenTab && !disableFixedHeaderVideo;

  const css = useCss();

  return (
    <OnWindowEvent event="scroll" handler={handleScroll}>
      <div
        className={css('placeholder', {
          // For some reason, making this position: relative prevents the background colors of child
          // elements from slightly protruding from the navigation bar.
          position: 'relative',
          zIndex,
          ...isFixed || onAFullScreenTab ? { minHeight: `${childContainerSize.height}px` } : {},
          '@media print': {
            display: 'none',
          },
        })}
        ref={placeholderNode}
      >
        <div
          className={css('container',
            isFixed && fixedSidebar
              ? {
                position: 'fixed',
                left: '0',
                right: `${CALL_SIDEBAR_WIDTH}px`,
                top: '0',
              }
              : isFixed && onAFullScreenTab
              ? {
                bottom: '0',
                display: 'flex',
                flexDirection: 'column',
                left: '0',
                position: 'fixed',
                right: '0',
                top: '0',
              }
              : isFixed
              ? {
                position: 'fixed',
                left: '0',
                right: '0',
                top: '0',
              }
              : onAFullScreenTab
              ? {
                background: PURE_WHITE,
                bottom: '0',
                display: 'flex',
                flexDirection: 'column',
                left: '0',
                paddingTop: `${HEADER_HEIGHT}px`,
                position: 'fixed',
                right: '0',
                top: '0',
              }
              : {},
          )}
        >
          <div
            ref={childContainerRef}
            className={css('childContainer', { position: 'relative', zIndex: Stack.ChildContainer })}
          >
            {children}
            {showOverheadVideo && pushContentDown && <CallVideosHorizontal />}
          </div>

          {/*
            Make sure to use a class that positions the content absolutely
            as to not push page content down when `pushContentDown` is
            `false`
          */}
          <div
            className={css('overheadContainer', {
              position: 'relative',
              margin: 0,
              height: 0,
              zIndex: Stack.OverheadContainer,
            })}
          >
            <div
              className={css('overhead', {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
              })}
              ref={extraOverheadContentContainerRef}
            >
              <div className={css('overheadCallContainer', { position: 'relative' })}>
                {showOverheadVideo && !pushContentDown && <CallVideosHorizontal />}
                <div
                  className={css('overlayNotificationsPositioning', {
                    bottom: '-25px',
                    left: '10px',
                    position: 'absolute',
                    right: '10px',
                    transform: 'translateY(100%)',
                  })}
                >
                  <EndLessonNotifier />
                </div>
              </div>
            </div>
          </div>

          {onAFullScreenTab && (
            <Fragment>
              <div className={css('fullScreenCallTabContainer', { width: '100%', zIndex: Stack.FullVideoContainer })}>
                {bigVideo ? <CallVideosFull layout={CallVideosLayout.Small} /> : <CallVideosHorizontal />}
              </div>

              <CallTabContent />
            </Fragment>
          )}
        </div>
      </div>
    </OnWindowEvent>
  );
});

export default StickyContainer;
